import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import { Form } from 'react-bootstrap';
import { isValidPhoneNumber } from '../utils/phoneUtils';

/**
 * Enhanced phone number input component for RxRooster application
 * - Provides real-time validation feedback
 * - Standardizes phone number format
 * - Provides clear visual indication of validity
 * 
 * @param {string} value - The current phone number value
 * @param {function} onChange - Function called when phone changes, receives normalized phone
 * @param {function} onBlur - Optional additional blur handler
 * @param {string} label - Optional custom label text
 * @param {boolean} required - Whether the field is required
 * @param {string} id - HTML ID for the input
 * @param {boolean} disabled - Whether the input is disabled
 * @param {object} inputProps - Additional props to pass to the phone input
 * @param {function} onValidityChange - Callback when validity changes
 */
const PhoneNumberInput = ({ 
  value, 
  onChange, 
  onBlur, 
  label = "Phone Number", 
  required = false,
  id = "phoneInput",
  disabled = false,
  inputProps = {},
  onValidityChange = null
}) => {
  const [touched, setTouched] = useState(false);
  const [isValid, setIsValid] = useState(false);
  
  // Validate phone number when value changes
  useEffect(() => {
    const valid = Boolean(value) && isValidPhoneNumber(value);
    setIsValid(valid);
    
    // Notify parent component of validity change if callback provided
    if (onValidityChange) {
      onValidityChange(valid);
    }
  }, [value, onValidityChange]);

  // Standardized change handler to ensure consistent format
  const handleChange = (phone) => {
    // Ensure phone always has country code
    if (phone && !phone.startsWith('+')) {
      if (phone.startsWith('1')) {
        phone = '+' + phone;
      } else {
        phone = '+1' + phone;
      }
    }
    onChange(phone);
  };

  // Handle blur event
  const handleBlur = (e) => {
    setTouched(true);
    
    // Allow custom blur handler to be called after standardized handling
    if (onBlur) {
      onBlur(e);
    }
  };

  // Determine validation state for Bootstrap
  const getValidationState = () => {
    if (!touched || !value) return null;
    return isValid ? 'is-valid' : 'is-invalid';
  };

  return (
    <Form.Group className="mb-3">
      {label && (
        <Form.Label htmlFor={id} className="fw-medium">
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
      )}
      
      <div className={`phone-input-container ${getValidationState()}`}>
        <PhoneInput
          country={'us'}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          inputProps={{
            id,
            required,
            disabled,
            className: `form-control ${getValidationState()}`,
            ...inputProps
          }}
          containerClass="mb-0 w-100"
          buttonClass="phone-dropdown-button"
          dropdownClass="phone-dropdown"
          searchClass="phone-search"
          enableSearch={true}
          disableSearchIcon={false}
          countryCodeEditable={false}
          preferredCountries={['us', 'ca']}
          placeholder="(555) 123-4567"
          specialLabel=""
        />
      </div>
      
      {touched && value && !isValid && (
        <Form.Text className="text-danger">
          Please enter a valid US phone number (10 digits)
        </Form.Text>
      )}
      
      {touched && isValid && (
        <Form.Text className="text-success">
          Valid phone number format
        </Form.Text>
      )}
      
      <style jsx="true">{`
        .phone-input-container {
          position: relative;
        }
        .phone-input-container .react-tel-input .form-control {
          width: 100%;
          padding-left: 52px;
          height: 42px;
          border-radius: 0.375rem;
        }
        .phone-input-container.is-invalid .react-tel-input .form-control {
          border-color: #dc3545;
          padding-right: calc(1.5em + 0.75rem);
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right calc(0.375em + 0.1875rem) center;
          background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
        }
        .phone-input-container.is-valid .react-tel-input .form-control {
          border-color: #198754;
          padding-right: calc(1.5em + 0.75rem);
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3e%3cpath fill='%23198754' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right calc(0.375em + 0.1875rem) center;
          background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
        }
        .phone-dropdown-button {
          border-radius: 0.375rem 0 0 0.375rem !important;
        }
      `}</style>
    </Form.Group>
  );
};

export default PhoneNumberInput;
